/**
 * Computes the aspect ratio for the cropping operation based on the selected orientation.
 * - If the base aspect ratio is taller than it is wide (portrait-like), it rotates the aspect ratio to landscape.
 * - Otherwise, returns the standard width/height ratio.
 * 
 * @param {Object} baseAspect - The base aspect ratio with properties: width and height.
 * @param {"landscape"} orientation - The selected orientation for cropping (only "landscape" supported).
 * @returns {number} - The computed aspect ratio (width divided by height) for the crop operation.
 */
export const getComputedAspect = (baseAspect, orientation) => {
    const ratio = baseAspect.width / baseAspect.height;

    if (orientation === "landscape" && baseAspect.width < baseAspect.height) {
        return baseAspect.height / baseAspect.width;
    }

    return ratio;
};

/**
 * Captures frame thumbnails from a hidden video element at 1-second intervals.
 * This is used to generate a visual preview of the trimmed section in the custom UI.
 *
 * @param {HTMLVideoElement} videoElement - The hidden video element used for seeking and frame capture.
 * @param {number} duration - The duration of the video in seconds.
 * @param {Object} cropBoxSize - The dimensions of the preview frames (width and height).
 * @returns {Promise<string[]>} - A promise that resolves to an array of base64-encoded PNG frames.
 */
export const captureFramesEverySecond = async (videoElement, duration, cropBoxSize) => {
    const MAX_FRAMES = 20; // Limit to 20 frames
    const interval = duration / MAX_FRAMES;

    const frames = [];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = cropBoxSize.width;
    canvas.height = cropBoxSize.height;

    for (let i = 0; i < MAX_FRAMES; i++) {
        const time = i * interval;
        await new Promise(resolve => {
            videoElement.currentTime = time;
            videoElement.onseeked = () => {
                ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
                frames.push(canvas.toDataURL("image/jpeg"));
                resolve();
            };
        });
    }

    return frames;
};
